import { useQuery } from '@tanstack/react-query'
import { useApi } from './useApi'

interface EnvironmentStatsResponse {
  transactions24h: number
}

// Create a queue to manage sequential requests
let requestQueue: Array<() => Promise<void>> = []
let isProcessing = false

const processQueue = async () => {
  if (isProcessing || requestQueue.length === 0) return

  isProcessing = true

  while (requestQueue.length > 0) {
    const request = requestQueue.shift()
    if (request) {
      await request()
      // Add a small delay between requests
      await new Promise(resolve => setTimeout(resolve, 100))
    }
  }

  isProcessing = false
}

const useEnvironmentStats = (mimicId: string, index: number = 0) => {
  const { callApi } = useApi()

  const fetchEnvironmentStats = async (): Promise<EnvironmentStatsResponse> => {
    return new Promise((resolve) => {
      const request = async () => {
        try {
          // Fetch transactions from the last 24 hours
          const now = new Date()
          const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000)

          // Format dates as ISO strings without milliseconds
          const startDate = yesterday.toISOString().split('.')[0]
          const endDate = now.toISOString().split('.')[0]

          const url = `/relayer-executor/environments/${mimicId}/transactions?startDate=${startDate}&endDate=${endDate}`
          const response = await callApi<any>(url)

          // The response is an array of transactions
          let count = 0
          if (Array.isArray(response)) {
            count = response.length
          } else if (response?.total) {
            count = response.total
          } else if (response?.data && Array.isArray(response.data)) {
            count = response.data.length
          }

          resolve({ transactions24h: count })
        } catch (error) {
          console.error('Error fetching environment stats:', error)
          resolve({ transactions24h: 0 })
        }
      }

      requestQueue.push(request)
      processQueue()
    })
  }

  return useQuery<EnvironmentStatsResponse>({
    queryKey: ['environmentStats', mimicId],
    queryFn: fetchEnvironmentStats,
    enabled: !!mimicId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    retryDelay: 1000,
  })
}

export default useEnvironmentStats
